import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/shared/api/useApi';
import type { PluginCatalogueItem, PluginMember } from '@/shared/api/types/plugins';
import { Button } from '@/shared/components';
import { PluginProblem } from './PluginProblem';
import { PluginMemberEditor } from './PluginMemberEditor';

export function PluginTeam({ plugin }: { plugin: PluginCatalogueItem }) {
  const api = useApi();
  const [editing, setEditing] = useState<PluginMember | null>(null);
  const query = useQuery({
    queryKey: ['plugin-members', plugin.installation?.id],
    queryFn: () => api.plugins.members(plugin.installation!.id),
    enabled: plugin.canManage && !!plugin.installation,
    retry: false,
  });
  if (!plugin.canManage)
    return (
      <p className="text-ink-soft">Ihr Workspace-Admin verwaltet die persönlichen Pluginrechte.</p>
    );
  if (!plugin.installation || plugin.installation.state === 'Revoked')
    return <p>Richten Sie das Plugin ein, bevor Sie Teamrechte vergeben.</p>;
  return (
    <div className="space-y-4">
      <p className="text-ink-soft text-sm">
        Jedes Mitglied verbindet seinen eigenen GuestAI-Login. Auch Admins benötigen eine
        persönliche Freigabe.
      </p>
      {plugin.installation.state !== 'Active' && (
        <p className="bg-amber/10 rounded-md p-4 text-sm">
          Die folgenden Rechte werden erst mit einer aktiven Verbindung wirksam.
        </p>
      )}
      <PluginProblem error={query.error} />
      {query.isPending && <p role="status">Team wird geladen…</p>}
      {query.isError && (
        <Button size="lg" onClick={() => void query.refetch()}>
          Erneut laden
        </Button>
      )}
      <ul className="divide-line divide-y">
        {query.data?.map((member) => (
          <li
            key={member.userId}
            className="flex flex-wrap items-center justify-between gap-4 py-4"
          >
            <div className="min-w-0">
              <h3 className="break-words font-semibold">{member.name}</h3>
              <p className="text-ink-soft text-sm">
                {member.scope === 'None'
                  ? 'Kein Zugriff'
                  : member.scope === 'TenantAll'
                    ? `Alle Apartments · ${member.actions.length} Aktionen`
                    : `${member.propertyIds.length} Apartments · ${member.actions.length} Aktionen`}
              </p>
            </div>
            <Button
              type="button"
              size="lg"
              onClick={() => setEditing(member)}
              aria-label={`Rechte für ${member.name} bearbeiten`}
            >
              Rechte bearbeiten
            </Button>
          </li>
        ))}
      </ul>
      {editing && (
        <PluginMemberEditor
          key={editing.userId}
          plugin={plugin}
          member={editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
